import React, { useState } from "react";
import {
  MessageSquare, Camera, FileCheck, Calculator, HardHat, ArrowRight,
  Star, Users, ShieldCheck, TrendingUp, FileText, Send, Search, Handshake, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AuthModal } from "@/components/AuthModal";
import { Pricing } from "@/components/Pricing";
import { ChatAssistant } from "@/components/tools/ChatAssistant";
import { PhotoDiagnostic } from "@/components/tools/PhotoDiagnostic";
import { QuoteEstimator } from "@/components/tools/QuoteEstimator";
import { ArtisansDirectory } from "@/components/tools/ArtisansDirectory";

const HERO_IMG = "https://images.unsplash.com/photo-1748063578185-3d68121b11ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB2aWxsYSUyMGFyY2hpdGVjdHVyZSUyMG5pZ2h0fGVufDB8fHx8MTc4OTg5NTM0MHww&ixlib=rb-4.1.0&q=85";
const TEAM_IMG = "https://images.pexels.com/photos/8961260/pexels-photo-8961260.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const QUICK = [
  { title: "Poser une question", desc: "Conseil d'expert IA 24/7", Icon: MessageSquare, target: "assistant-btp", testId: "hero-quickaction-question" },
  { title: "Envoyer des photos", desc: "Diagnostic défauts & fissures", Icon: Camera, target: "diagnostic-ia", testId: "hero-quickaction-photo" },
  { title: "Faire analyser un devis", desc: "Vérification sous 24h", Icon: FileCheck, target: "tarifs", testId: "hero-quickaction-devis" },
  { title: "Estimer mes travaux", desc: "Calculateur budget instantané", Icon: Calculator, target: "estimation", testId: "hero-quickaction-estimation" },
  { title: "Trouver un artisan", desc: "Réseau labellisé & assuré", Icon: HardHat, target: "artisans", testId: "hero-quickaction-artisan" },
];

const STATS = [
  { value: "4.8/5", label: "Note moyenne", Icon: Star },
  { value: "+2 000", label: "Projets accompagnés", Icon: TrendingUp },
  { value: "+1 500", label: "Artisans certifiés", Icon: Users },
  { value: "98%", label: "Clients satisfaits", Icon: ShieldCheck },
];

const STEPS = [
  { num: "01", title: "Envoyez vos devis / plans", desc: "Téléchargez vos fichiers ou décrivez votre projet en 2 minutes.", Icon: FileText },
  { num: "02", title: "Analyse IA & métreur", desc: "Croisement avec les référentiels de prix et les normes DTU.", Icon: Search },
  { num: "03", title: "Vous recevez le rapport", desc: "Anomalies, oublis et marges de négociation détaillés.", Icon: Send },
  { num: "04", title: "Décidez en confiance", desc: "Échange avec nos experts et sélection d'artisans certifiés.", Icon: Handshake },
];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Landing() {
  const [authOpen, setAuthOpen] = useState(false);
  const [audience, setAudience] = useState("particulier");

  return (
    <div className="bg-[#0A0F1A]">
      <Header onLogin={() => setAuthOpen(true)} />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />

      {/* HERO */}
      <section id="accueil" className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="villa moderne" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0F1A]/80 via-[#0A0F1A]/70 to-[#0A0F1A]" />
          <div className="absolute inset-0 idb-noise" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="idb-fade-up">
              <span className="inline-flex items-center gap-2 text-xs font-medium text-[#00C3A7] bg-[#00C3A7]/10 border border-[#00C3A7]/20 rounded-full px-4 py-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00C3A7] idb-pulse-dot" />
                Nouveau : IA générative certifiée DTU & Bâtiment France 2026
              </span>
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05]">
                VOS TRAVAUX,<br /><span className="idb-gradient-text">ENFIN SIMPLIFIÉS.</span>
              </h1>
              <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
                ID-BAT PRO accompagne particuliers et professionnels dans tous leurs projets de
                construction, rénovation et aménagement. Conseils, artisans qualifiés, analyses IA :
                tout pour réussir vos projets.
              </p>

              <div className="mt-8 inline-flex rounded-full bg-white/5 border border-white/10 p-1" role="tablist">
                {[{ v: "particulier", l: "Je suis Particulier", tid: "tab-particulier" }, { v: "professionnel", l: "Je suis Professionnel", tid: "tab-professionnel" }].map((t) => (
                  <button
                    key={t.v}
                    data-testid={t.tid}
                    onClick={() => setAudience(t.v)}
                    className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                      audience === t.v ? "bg-[#0D6EFD] text-white shadow-lg shadow-[#0D6EFD]/30" : "text-white/70"
                    }`}
                  >
                    {t.l}
                  </button>
                ))}
              </div>

              <div className="mt-6 flex items-center gap-4">
                <div className="flex -space-x-2">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0D6EFD] to-[#00C3A7] border-2 border-[#0A0F1A]" />
                  ))}
                </div>
                <div className="text-sm text-white/70">
                  <span className="text-white font-semibold">4,8/5</span> · + de 2 000 projets accompagnés
                </div>
              </div>
            </div>

            {/* Quick action cards */}
            <div className="idb-fade-up" style={{ animationDelay: "150ms" }}>
              <div className="rounded-3xl bg-[#121A29]/80 backdrop-blur-xl border border-white/10 p-6">
                <p className="text-white/60 text-sm mb-4">
                  Bonjour 👋 {audience === "particulier" ? "Quel est votre besoin ?" : "Développez votre activité avec ID-BAT PRO"}
                </p>
                <div className="space-y-2.5">
                  {QUICK.map((q) => (
                    <button
                      key={q.title}
                      data-testid={q.testId}
                      onClick={() => scrollTo(q.target)}
                      className="w-full group flex items-center gap-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-[#0D6EFD]/40 p-4 transition-all text-left"
                    >
                      <div className="w-11 h-11 rounded-xl bg-[#0D6EFD]/15 flex items-center justify-center shrink-0">
                        <q.Icon className="w-5 h-5 text-[#00C3A7]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm">{q.title}</p>
                        <p className="text-white/50 text-xs">{q.desc}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#0D6EFD] group-hover:translate-x-1 transition-all" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#0A0F1A] border-y border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.label} className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#0D6EFD]/10 flex items-center justify-center">
                <s.Icon className="w-5 h-5 text-[#00C3A7]" />
              </div>
              <div>
                <p className="text-2xl font-extrabold text-white">{s.value}</p>
                <p className="text-white/50 text-xs">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PHOTO DIAGNOSTIC */}
      <section id="diagnostic-ia" className="py-24 bg-[#0A0F1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="Diagnostic Photo IA"
            title="Une fissure ? Une infiltration ? Photographiez, l'IA analyse."
            desc="Upload d'une photo de chantier et obtenez en quelques secondes le niveau de gravité, les causes probables et les préconisations de reprise."
          />
          <PhotoDiagnostic />
        </div>
      </section>

      {/* AI CHAT */}
      <section id="assistant-btp" className="py-24 bg-[#121A29] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHead
              align="left"
              eyebrow="Assistant IA Expert Bâtiment"
              title="Toutes vos questions travaux, une réponse d'expert immédiate."
              desc="DTU, RE2020, norme électrique NF C 15-100, aides MaPrimeRénov', prix au m²… Notre assistant IA vous répond 24/7 en français technique."
            />
            <ul className="space-y-3">
              {["Conseils matériaux & mise en œuvre", "Vérification de la cohérence d'un devis", "Aides et subventions rénovation 2026", "Normes et réglementation en vigueur"].map((f) => (
                <li key={f} className="flex items-center gap-3 text-white/80">
                  <CheckCircle2 className="w-5 h-5 text-[#00C3A7]" /> {f}
                </li>
              ))}
            </ul>
          </div>
          <ChatAssistant />
        </div>
      </section>

      {/* ESTIMATOR */}
      <section id="estimation" className="py-24 bg-[#0A0F1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="Estimation Budget Travaux"
            title="Estimez le budget de votre chantier en 30 secondes."
            desc="Calculateur intelligent selon le corps d'état, la surface, la finition et votre région, avec un conseil personnalisé de notre IA métreur."
          />
          <div className="rounded-3xl bg-[#121A29] border border-white/10 p-6 sm:p-8">
            <QuoteEstimator />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-[#121A29] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Comment ça marche ?" title="Un accompagnement fluide en 4 étapes." desc="Simple, transparent et rassurant, du premier devis à la fin du chantier." />
          <div className="grid md:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.num} className="relative rounded-2xl bg-white/5 border border-white/10 p-6 idb-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <span className="text-5xl font-extrabold text-white/10">{s.num}</span>
                <div className="w-11 h-11 rounded-xl bg-[#0D6EFD]/15 flex items-center justify-center -mt-4 mb-3">
                  <s.Icon className="w-5 h-5 text-[#00C3A7]" />
                </div>
                <h4 className="text-white font-semibold">{s.title}</h4>
                <p className="text-white/50 text-sm mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <Pricing />

      {/* ARTISANS */}
      <section id="artisans" className="py-24 bg-[#0A0F1A] relative overflow-hidden">
        <img src={TEAM_IMG} alt="artisans" className="absolute top-0 right-0 w-1/3 h-full object-cover opacity-10 hidden lg:block" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Artisans Qualifiés" title="+1 500 artisans certifiés, assurés et notés." desc="Filtrez par métier, label RGE / Qualibat / Décennale et géolocalisation. Prise de contact directe." />
          <ArtisansDirectory />
        </div>
      </section>

      {/* PRO CTA */}
      <section id="pro" className="py-24 bg-[#121A29] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-[#0A0F1A] border border-white/10 p-8 sm:p-12 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0D6EFD] mb-3">Espace Professionnels</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">Recevez des leads qualifiés près de chez vous.</h2>
              <p className="mt-4 text-white/60">Tableau de bord, demandes de devis, suivi de chantier et statistiques de performance. Développez votre activité sereinement.</p>
              <Button onClick={() => setAuthOpen(true)} className="mt-6 bg-[#0D6EFD] hover:bg-[#0b5ed7] rounded-full h-12 px-8" data-testid="pro-signup-btn">
                Rejoindre l'espace Pro <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[{ v: "128", l: "Leads reçus" }, { v: "42", l: "Demandes en cours" }, { v: "26", l: "Projets gagnés" }, { v: "4.8/5", l: "Avis moyen" }].map((k) => (
                <div key={k.l} className="rounded-2xl bg-white/5 border border-white/10 p-5">
                  <p className="text-3xl font-extrabold text-white">{k.v}</p>
                  <p className="text-white/50 text-sm mt-1">{k.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const SectionHead = ({ eyebrow, title, desc, align = "center" }) => (
  <div className={`max-w-2xl mb-12 ${align === "center" ? "mx-auto text-center" : ""}`}>
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0D6EFD] mb-3">{eyebrow}</p>
    <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">{title}</h2>
    {desc && <p className="mt-4 text-white/60">{desc}</p>}
  </div>
);
